import { useState, useEffect } from "react"
import { usePageContext } from "vike-react/usePageContext"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { GetDownloadable, GetChangelog } from "aldoo-ra/Downloadables/api"
import { changelogLines } from "aldoo-ra/Downloadables/changelog-lines"
import { VariantReader } from "aldoo-ra/VariantReader"
import { TagIcon } from "@heroicons/react/24/outline"

const DOWNLOADABLE_ID = "snap-macos"
const CHANGELOG_LIMIT = 20

// Fixed locale + UTC so the SSR markup matches the client hydration pass
// (toLocaleDateString() would differ between server and browser).
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
})
const formatDate = (value) => {
  if (!value) return ""
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? "" : dateFormatter.format(d)
}

// SSR provides `snapRelease` (see pages/+onBeforeRender.js). Client-side
// navigation to the home page also gets it via passToClient, so the fetch
// below is only a fallback when the SSR lookup failed.
function useSnapRelease() {
  const pageContext = usePageContext()
  const [release, setRelease] = useState(pageContext?.snapRelease || null)

  useEffect(() => {
    if (release) return
    let cancelled = false
    Promise.all([
      GetDownloadable(DOWNLOADABLE_ID).catch(() => null),
      GetChangelog(DOWNLOADABLE_ID, { limit: CHANGELOG_LIMIT }).catch(() => null),
    ]).then(([info, log]) => {
      if (cancelled) return
      const latest = info?.downloadable?.latest
      if (!latest?.version) return
      setRelease({
        name: info.downloadable.name,
        version: latest.version,
        created: latest.created,
        changelog: (log?.changelog || []).filter((e) => e?.changelog),
      })
    })
    return () => {
      cancelled = true
    }
  }, [release])

  return release
}

// Same markup + classes as the account Downloads widget (Downloadables
// "snap" variant). Plain div/span — no <article>/<h3>/<p>, which would pick
// up the blog post typography from Layout.css.
function ChangelogEntries({ entries, versionLabel }) {
  const v = VariantReader.useVariant({ componentName: "Downloadables", variant: "snap" })
  return (
    <div className={v.changelog_entries}>
      {entries.map((entry) => (
        <div key={entry.version} className="px-4 md:px-0">
          <div className="flex items-baseline gap-3">
            <span className={v.changelog_version}>
              {versionLabel} {entry.version}
            </span>
            {entry.created && (
              <span className={v.changelog_date}>{formatDate(entry.created)}</span>
            )}
          </div>
          <ul className={v.changelog_text}>
            {changelogLines(entry.changelog).map((line, i) => (
              <li key={i} className={v.changelog_line}>
                <span className={v.changelog_bullet} aria-hidden="true">–</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

/**
 * "Latest Version x.y.z" outlined button → release notes dialog with the
 * full changelog and the same Download CTA as the hero.
 *
 * The changelog is also rendered server-side in a visually hidden block so
 * crawlers index the release notes even though users only see the button.
 */
export default function ReleaseNotes({ texts, renderDownload, className = "" }) {
  const [open, setOpen] = useState(false)
  const release = useSnapRelease()

  if (!release?.version) return null

  const versionLabel = texts?.version_label || "Version"
  const latestLabel = texts?.latest_version_label || "Latest Version"
  const dialogTitle = texts?.release_notes_title || "What's new"
  const entries = release.changelog || []
  const appName = release.name || "Aldoo Snap"

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setOpen(true)}
        className={className}
      >
        <TagIcon className="w-4 h-4" />
        {latestLabel} {release.version}
      </Button>

      {/* Crawler-visible release notes (SSR). Hidden from sighted users — the
          dialog is the interactive view of the same content. */}
      <section className="sr-only" aria-hidden="true">
        <h2>{appName} — {latestLabel} {release.version}</h2>
        {entries.length > 0 && <ChangelogEntries entries={entries} versionLabel={versionLabel} />}
      </section>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        variant="snap_medium"
        closeOnEscape={true}
        closeOnClickOutside={true}
        title={`${dialogTitle} · ${release.version}`}
        className="md:w-[680px]"
        styleOverrides={{
          footer: "bg-paper px-4 pt-4 pb-2 md:pb-0 flex justify-center",
        }}
        footerContent={
          <div className="w-full flex justify-center">
            {renderDownload?.()}
          </div>
        }
      >
        <div className="md:border-b md:border-bordered pb-3 pt-1 mb-4 md:mb-6">
          <Typography variant="txt_dim" className="block text-center md:text-left">
            {appName}
            {release.created ? ` · ${formatDate(release.created)}` : ""}
          </Typography>
        </div>

        {entries.length > 0 ? (
          <ChangelogEntries entries={entries} versionLabel={versionLabel} />
        ) : (
          <Typography variant="txt_dim" className="block text-center">
            {texts?.release_notes_empty || "No release notes yet."}
          </Typography>
        )}
      </Modal>
    </>
  )
}
